import React, { useState } from 'react';
import axios from 'axios';
import { useFormikContext } from 'formik';
import { isEmpty } from 'lodash';

import WithConfirm from '~/components/ConfirmModal';
import AlertBanner from '~/components/core/AlertBanner';
import Button from '~/components/core/Atomic/Buttons/Button';
import type { IncidentConfigurationModelGlobalFieldsStatus } from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

import copies from './copies.json';

const TEXTS = copies.global_fields_drift;
const DRIFT_KINDS = ['missing', 'extra', 'stale'] as const;

interface GlobalFieldsDriftBannerProps {
  globalFieldsStatus?: IncidentConfigurationModelGlobalFieldsStatus | null;
  incidentConfigurationRoute: string;
  reloadConfiguration: () => Promise<unknown>;
}

// Informational: the form stays editable while it shows, so a configurator whose repair fails is never
// left with a form they cannot save. Rendered inside the editor's Formik: the editors reinitialize from
// the refetched configuration, so a repair asks first when it would discard unsaved edits.
const GlobalFieldsDriftBanner: React.FC<GlobalFieldsDriftBannerProps> = ({
  globalFieldsStatus,
  incidentConfigurationRoute,
  reloadConfiguration,
}) => {
  const [isFixing, setIsFixing] = useState(false);
  const { dirty } = useFormikContext();

  if (!globalFieldsStatus || globalFieldsStatus.in_sync) {
    return null;
  }

  const fixAndReload = async () => {
    setIsFixing(true);
    try {
      await axios.post(`${incidentConfigurationRoute}/reconcile_global_fields`);
      await reloadConfiguration();
    } catch (error) {
      await reportAxiosError(error);
    } finally {
      setIsFixing(false);
    }
  };

  return (
    <div className="ml-4 bg-slate-200 px-12 pb-12">
      <AlertBanner
        alertType={AlertBanner.ALERT_TYPES.WARNING}
        title={TEXTS.title}
        withIcon
        note={
          <div className="flex items-center justify-between gap-12">
            <div className="flex flex-col">
              {DRIFT_KINDS.filter((kind) => !isEmpty(globalFieldsStatus[kind])).map((kind) => (
                <span key={kind}>{TEXTS[kind].replace('{keys}', (globalFieldsStatus[kind] ?? []).join(', '))}</span>
              ))}
            </div>
            <WithConfirm
              title={TEXTS.discard_confirm.title}
              contentText={TEXTS.discard_confirm.text}
              primaryButtonName={TEXTS.fix_and_reload}
              shouldCloseOnPrimary
              disableConfirm={!dirty}
            >
              <Button variant="outlined" color="primary" disabled={isFixing} onClick={fixAndReload}>
                {TEXTS.fix_and_reload}
              </Button>
            </WithConfirm>
          </div>
        }
      />
    </div>
  );
};

export default GlobalFieldsDriftBanner;
